import { useState, useCallback } from 'react';
import dayjs from 'dayjs';

/**
 * Custom hook for handling date range logic in report forms
 * Provides standardized date validation, formatting, and error handling
 */
export const useDateRangeHandler = (reportData, setReportData, errors, setErrors, options = {}) => {
  
  const {
    enableValidation = false,      // Enable date range validation and error handling
    autoSetEndDate = false,        // Auto-set end date when start date changes
    maxYearRange = 1              // Maximum years allowed between start and end date
  } = options;

  /**
   * Formats date with timezone adjustment
   * @param {Date|string} date - The date to format 
   * @returns {string} - ISO formatted date string (YYYY-MM-DD)
   */
  const formatDateWithTimezone = useCallback((date) => {
    const dateObj = new Date(date);
    dateObj.setMinutes(dateObj.getMinutes() - dateObj.getTimezoneOffset());
    return dateObj.toISOString().split('T')[0];
  }, []);

  /**
   * Clears date-related errors
   */
  const clearDateErrors = useCallback(() => {
    if (enableValidation) {
      setErrors(prev => {
        const newErrors = {...prev};
        delete newErrors.custom_start_date;
        delete newErrors.custom_end_date;
        return newErrors;
      });
    }
  }, [setErrors, enableValidation]);

  /**
   * Validates date range (max year difference)
   * @param {string} startDate - Start date in YYYY-MM-DD format
   * @param {string} endDate - End date in YYYY-MM-DD format
   * @returns {boolean} - Whether the date range is valid
   */
  const validateDateRange = useCallback((startDate, endDate) => {
    if (!enableValidation || !startDate || !endDate) return true;
    
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    const maxAllowedEndDate = start.add(maxYearRange, 'year');
    
    return !end.isAfter(maxAllowedEndDate, 'day');
  }, [enableValidation, maxYearRange]);

  /**
   * Handles start date change with validation and auto-setting end date
   * @param {Date|string} startDate - The selected start date
   * @param {boolean} overrideAutoSet - Override the default auto-setting behavior
   */
  const handleStartDateChange = useCallback((startDate, overrideAutoSet = null) => {
    
    if (enableValidation) {
      clearDateErrors();
    }
    
    const formattedStartDate = formatDateWithTimezone(startDate);
    
    let newReportData = { 
      ...reportData, 
      custom_start_date: formattedStartDate
    };

    const shouldAutoSet = overrideAutoSet !== null ? overrideAutoSet : autoSetEndDate;

    if (shouldAutoSet && enableValidation) {
      // Calculate max end date (maxYearRange from start or today, whichever is earlier)
      const maxEndFromStart = dayjs(formattedStartDate).add(maxYearRange, 'year');
      const today = dayjs();
      const maxEndDate = maxEndFromStart.isBefore(today) ? maxEndFromStart : today;
      
      // Automatically set end date to max allowed date
      const autoEndDate = maxEndDate.format('YYYY-MM-DD');
      newReportData.custom_end_date = autoEndDate;
    }
    
    setReportData(newReportData);
  }, [reportData, setReportData, clearDateErrors, formatDateWithTimezone, autoSetEndDate, enableValidation, maxYearRange]);

  /**
   * Handles end date change with validation
   * @param {Date|string} endDate - The selected end date
   * @param {boolean} overrideValidation - Override the default validation behavior
   */
  const handleEndDateChange = useCallback((endDate, overrideValidation = null) => {
    const shouldValidate = overrideValidation !== null ? overrideValidation : enableValidation;

    if (shouldValidate && !reportData.custom_start_date) {
      setErrors(prev => ({
        ...prev, 
        custom_end_date: 'Please select a start date first'
      }));
      return; 
    }
    
    const formattedEndDate = formatDateWithTimezone(endDate);
    
    if (shouldValidate && !validateDateRange(reportData.custom_start_date, formattedEndDate)) {
      setErrors(prev => ({
        ...prev, 
        custom_end_date: `End date cannot be more than ${maxYearRange} year${maxYearRange > 1 ? 's' : ''} from start date`
      }));
      return; 
    }
    
    // Clear errors if validation passes or is disabled
    if (enableValidation) {
      clearDateErrors();
    }
    
    setReportData({ 
      ...reportData, 
      custom_end_date: formattedEndDate 
    });
  }, [reportData, setReportData, setErrors, clearDateErrors, formatDateWithTimezone, validateDateRange, enableValidation, maxYearRange]);

  return {
    handleStartDateChange,
    handleEndDateChange,
    formatDateWithTimezone,
    validateDateRange,
    clearDateErrors
  };
};